import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getUserInfo } from "../store/rootReducer";
import CustomButton from "../components/Button";
import Loader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";
import EditQuoteModal from "../components/EditQuoteModal";
import ReviseEmiTermsModal from "../components/ReviseEmiTermsModal";
import { BackLink, DefRow, Restrict, Sub2, Tag } from "../components/DashTable";
import ActionDrawer, { UI, btnSx } from "../components/ActionDrawer";
import scanOUpiQr from "../assets/scano-upi-qr.jpg";

/* MUI colour names mapped onto the dashboard tag tones. */
const TONES = {
  success: "g",
  error: "b",
  warning: "s",
  info: "bl",
  primary: "stage",
  secondary: "stage",
  default: "i",
};

/* Secondary actions, drawn as outlined rather than filled buttons. */
const GHOST_ACTIONS = [
  "Cancel",
  "Edit quote",
  "Copy link",
  "Open link",
  "Resend link",
  "Mark under review",
];

/* CustomButton wearing the dashboard button styles. Behaviour is unchanged:
   CustomButton spreads props after its own sx, so ours wins. */
const Btn = ({ variant, sx, ...props }) => {
  const tone =
    variant || (GHOST_ACTIONS.includes(props.text) ? "ghost" : "solid");
  return (
    <CustomButton {...props} sx={{ ...btnSx(tone, props.disabled), ...sx }} />
  );
};

/* Card panel used down both columns of this page. */
const Card = ({ children, sx }) => (
  <Box
    sx={{
      bgcolor: UI.surface,
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "8px",
      padding: "16px 18px",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Box>
);

/* Read only figure block used inside the dialogs. */
const Stat = ({ label, value }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      bgcolor: UI.bg2,
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "6px",
      padding: "10px 12px",
    }}
  >
    <Box sx={{ fontSize: 11, color: UI.ink3 }}>{label}</Box>
    <Box sx={{ fontSize: 14, fontWeight: 500, color: UI.ink, marginTop: "2px" }}>
      {value}
    </Box>
  </Box>
);

const dialogPaperSx = {
  bgcolor: UI.surface,
  backgroundImage: "none",
  border: "1px solid",
  borderColor: UI.line,
  borderRadius: "10px",
};

const dialogTitleSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "10px",
  padding: "16px 18px",
  borderBottom: "1px solid",
  borderColor: UI.line,
};

const DialogHeading = ({ title, sub }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={{ fontSize: 16, fontWeight: 500, color: UI.ink, overflowWrap: "anywhere" }}>{title}</Box>
    {sub ? (
      <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px", overflowWrap: "anywhere" }}>{sub}</Box>
    ) : null}
  </Box>
);

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Matches CHECKLIST_DEFINITION in order-installation.service.js.
const CHECKLIST_PHOTO_LABELS = {
  unit_in_place: "Unit in place",
  serial_plate: "Serial plate",
  test_scan_result: "First test scan",
  checklist_form: "Technician activation and handover checklist",
};

const PRODUCT_LABELS = { Air: "scanO Air", Nova: "scanO Nova Rover" };

const STATUS_META = {
  agreement: { label: "AGREEMENT", color: "info" },
  onboarding: { label: "ONBOARDING", color: "info" },
  mockup: { label: "MOCKUP", color: "warning" },
  ready_to_dispatch: { label: "READY TO DISPATCH", color: "primary" },
  dispatched: { label: "DISPATCHED", color: "secondary" },
  installation: { label: "INSTALLATION", color: "warning" },
  activated: { label: "ACTIVATED", color: "success" },
};

const CALL_STATUS_TAG = {
  not_offered: { label: "NOT OFFERED", tone: "i" },
  offered: { label: "OFFERED", tone: "s" },
  booked: { label: "BOOKED", tone: "bl" },
};

const Row = ({ label, value, last }) => (
  <DefRow label={label} value={value} last={last} />
);

/* Breaks out of the parent Card's padding to draw a full-width divider under
   the header, matching the prototype's separate .ph/.pb pane sections. */
const CardHeader = ({ title, tag }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "10px",
      margin: "-16px -18px 12px",
      padding: "13px 18px",
      borderBottom: "1px solid",
      borderBottomColor: UI.line,
    }}
  >
    <Box sx={{ fontSize: 13, fontWeight: 500, color: UI.ink, minWidth: 0, overflowWrap: "anywhere" }}>{title}</Box>
    {tag}
  </Box>
);

/* Full lifecycle, header to header with the prototype's stepper. Our backend
   only models Agreement onward as a status enum — the pre-agreement steps are
   derived from quote/EMI fields, same as computeStage() in orders.js. */
const STAGES = [
  "Draft",
  "Quotation",
  "Advance payment",
  "EMI",
  "Agreement",
  "Onboarding",
  "Mockup",
  "Pre-dispatch",
  "Dispatched",
  "Installation",
  "Activated",
];

const POST_AGREEMENT_STAGE = {
  onboarding: "Onboarding",
  mockup: "Mockup",
  ready_to_dispatch: "Pre-dispatch",
  dispatched: "Dispatched",
  installation: "Installation",
  activated: "Activated",
};

const computeCurrentStage = (order) => {
  if (POST_AGREEMENT_STAGE[order.status]) return POST_AGREEMENT_STAGE[order.status];
  if (order.purchaseAgreement?.signed) return "Agreement";
  if (order.quote?.paymentType === "emi" && order.emi?.status && order.emi.status !== "not_started")
    return "EMI";
  if (order.quotePdf?.signed) return "Advance payment";
  if (order.quotePdf?.sent) return "Quotation";
  return "Draft";
};

// Whether a stage *before* the current one actually happened — the order's
// status only tells us the furthest point reached, not that every earlier
// step along the way is genuinely complete (e.g. an agreement can be signed
// before the advance is paid; nothing enforces that ordering).
const isStageDone = (stage, order, advancePaid) => {
  switch (stage) {
    case "Draft":
      return Boolean(order.quotePdf?.sent);
    case "Quotation":
      return Boolean(order.quotePdf?.signed);
    case "Advance payment":
      return ["advance", "emi"].includes(order.quote?.paymentType) && Boolean(advancePaid);
    case "EMI":
      return order.quote?.paymentType === "emi" && order.emi?.status === "approved";
    case "Agreement":
      return Boolean(order.purchaseAgreement?.signed);
    case "Onboarding":
      return ["mockup", "ready_to_dispatch", "dispatched", "installation", "activated"].includes(order.status);
    case "Mockup":
      return ["ready_to_dispatch", "dispatched", "installation", "activated"].includes(order.status);
    case "Pre-dispatch":
      return ["dispatched", "installation", "activated"].includes(order.status);
    case "Dispatched":
      return ["installation", "activated"].includes(order.status);
    case "Installation":
      return order.status === "activated";
    default:
      return false;
  }
};

const Stepper = ({ order, advancePaid }) => {
  const current = computeCurrentStage(order);
  const currentIndex = Math.max(STAGES.indexOf(current), 0);
  return (
    <>
      {/* MOBILE: the 11-label strip below doesn't fit a phone even scrolled —
          a compact "stage X of N" line + progress bar reads as actually
          responsive instead of just technically scrollable. */}
      <Box sx={{ display: { xs: "block", sm: "none" }, marginTop: "18px" }}>
        <Box sx={{ fontSize: 12, color: UI.ink3, marginBottom: "6px" }}>
          Stage {currentIndex + 1} of {STAGES.length} ·{" "}
          <Box component="span" sx={{ color: UI.pDark, fontWeight: 500 }}>
            {current}
          </Box>
        </Box>
        <Box sx={{ height: 4, borderRadius: "2px", bgcolor: UI.line, overflow: "hidden" }}>
          <Box
            sx={{
              height: "100%",
              width: `${((currentIndex + 1) / STAGES.length) * 100}%`,
              bgcolor: UI.p,
              borderRadius: "2px",
            }}
          />
        </Box>
      </Box>

      {/* TABLET/DESKTOP: full stage-by-stage strip */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0, marginTop: "18px", overflowX: "auto" }}>
        {STAGES.map((stage, i) => {
          const isNow = i === currentIndex;
          const done = i < currentIndex && isStageDone(stage, order, advancePaid);
          return (
          <Box
            key={stage}
            sx={{
              flex: 1,
              minWidth: "80px",
              padding: "7px 4px 0",
              borderTop: "3px solid",
              borderTopColor: done || isNow ? UI.p : UI.line,
              fontSize: 11,
              color: done ? UI.ink3 : isNow ? UI.pDark : UI.ink4,
              fontWeight: isNow ? 500 : 400,
            }}
          >
            {stage}
          </Box>
          );
        })}
      </Box>
    </>
  );
};

/* Best-effort "who needs to act next" summary — mirrors the canned per-order
   string in the UI spec closely enough to be useful, not a precise replica. */
const computeWaitingOn = (order) => {
  if (order.hold?.held) return "Accounts";
  if (POST_AGREEMENT_STAGE[order.status]) {
    if (order.status === "onboarding" && !order.onboardingCall?.confirmed) return "Customer";
    if (order.status === "mockup") return "Ops Associate";
    if (order.status === "ready_to_dispatch") return "Ops Associate";
    return "—";
  }
  if (order.purchaseAgreement && !order.purchaseAgreement.signed) return "Customer";
  if (order.quote?.paymentType === "emi" && order.emi?.status === "rejected") return "You";
  if (order.quotePdf?.sent && !order.quotePdf?.signed) return "Customer";
  return "You";
};

/* Documents rail — QT/PA/MK rows, matching the prototype's Documents pane.
   Only renders rows for documents that actually exist on this order. */
const DocRow = ({ code, title, sub, onOpen, onDownload, onEmail, emailLabel, emailBusy }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "10px",
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "4px",
      padding: "10px 12px",
      marginBottom: "8px",
      fontSize: 13,
    }}
  >
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "4px",
        bgcolor: UI.p50,
        color: UI.pDark,
        display: "grid",
        placeItems: "center",
        fontSize: 10,
        fontWeight: 500,
        flexShrink: 0,
      }}
    >
      {code}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {title}
      <Sub2>{sub}</Sub2>
    </Box>
    {onOpen ? (
      <Box component="button" type="button" onClick={onOpen} sx={pdfLinkSx}>
        <OpenInNewIcon sx={{ fontSize: 15 }} />
        Open
      </Box>
    ) : null}
    {onDownload ? (
      <Box component="button" type="button" onClick={onDownload} sx={pdfLinkSx}>
        <DownloadIcon sx={{ fontSize: 15 }} />
        Download
      </Box>
    ) : null}
    {onEmail ? (
      <Box
        component="button"
        type="button"
        onClick={onEmail}
        disabled={emailBusy}
        sx={pdfLinkSx}
      >
        <EmailIcon sx={{ fontSize: 15 }} />
        {emailLabel || "Email to doctor"}
      </Box>
    ) : null}
  </Box>
);

const DocumentsPane = ({
  order,
  onOpenQuote,
  onOpenInvoice,
  onOpenMockup,
  onDownloadMockup,
  onOpenPurchaseAgreement,
}) => {
  const hasLogo = Boolean(order.logo?.url);
  const hasInvoice = Boolean(order.invoice?.zohoInvoiceId || order.invoice?.uploadedManually);
  const hasAny =
    order.quotePdf?.sent || order.quotePdf?.signed || hasInvoice || hasLogo;
  return (
    <Card>
      <CardHeader title="Documents" />
      {order.quotePdf?.sent && (
        <DocRow
          code="QT"
          title="Quotation"
          sub={order.quotePdf?.signed ? "Accepted" : "Sent"}
          onOpen={onOpenQuote}
        />
      )}
      {order.quotePdf?.signed && (
        <DocRow
          code="PA"
          title="Purchase agreement"
          sub={
            order.purchaseAgreement.signed
              ? `Signed${
                  order.purchaseAgreement.signedAt
                    ? ", " + new Date(order.purchaseAgreement.signedAt).toLocaleDateString()
                    : ""
                }`
              : order.purchaseAgreement.sent
                ? "Sent, awaiting signature"
                : "Not sent"
          }
          onOpen={order.purchaseAgreement?.documentUrl ? onOpenPurchaseAgreement : undefined}
        />
      )}
      {hasInvoice && (
        <DocRow
          code="INV"
          title={order.invoice.invoiceNumber || "Tax invoice"}
          sub={
            order.invoice?.uploadedManually
              ? "Uploaded manually — see the Tax invoice card for actions"
              : "Raised — see the Tax invoice card for actions"
          }
          onOpen={order.invoice.url ? onOpenInvoice : undefined}
        />
      )}
      {hasLogo && (
        <DocRow
          code="LG"
          title={order.logo.fileName || "Clinic logo"}
          sub="Uploaded by the customer"
          onOpen={onOpenMockup}
          onDownload={onDownloadMockup}
        />
      )}
      {!hasAny && <Box sx={{ color: UI.ink3, fontSize: 13 }}>No documents yet.</Box>}
    </Card>
  );
};

const pdfLinkSx = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  border: 0,
  bgcolor: "transparent",
  padding: 0,
  cursor: "pointer",
  color: UI.p,
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "inherit",
  "&:hover": { textDecoration: "underline" },
};

const QuotePdfRow = ({ onOpen, onDownload }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "10px 12px",
      borderRadius: "6px",
      border: "1px solid",
      borderColor: UI.line,
      bgcolor: UI.bg2,
    }}
  >
    <DescriptionIcon sx={{ fontSize: 17, color: UI.ink3 }} />
    <Box sx={{ flex: 1, fontSize: 13, color: UI.ink }}>Quote PDF</Box>
    <Box component="button" type="button" onClick={onOpen} sx={pdfLinkSx}>
      <OpenInNewIcon sx={{ fontSize: 15 }} />
      Open
    </Box>
    <Box component="button" type="button" onClick={onDownload} sx={pdfLinkSx}>
      <DownloadIcon sx={{ fontSize: 15 }} />
      Download
    </Box>
  </Box>
);

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");
  const userInfo = useSelector(getUserInfo);
  const userRoles = Array.isArray(userInfo?.role)
    ? userInfo.role
    : [userInfo?.role].filter(Boolean);
  // Voiding a recorded payment is admin-only, same as the backend route.
  const isAdmin = userRoles.includes("admin");
  // Matches ACCESS.site in the UI spec — installation detail is for the
  // roles actually doing the install, not every role that can open an order.
  const canSeeSiteDetail = ["admin", "ops_associate", "ops_lead", "technician", "engagement", "manager"].some((role) =>
    userRoles.includes(role),
  );
  // The UPI QR is for whoever is physically on site collecting payment —
  // ops/admin don't need it cluttering their view of this card.
  const isTechnicianViewer = userRoles.includes("technician");
  // Mockup/clinic-logo approval is a sales/ops concern — Engagement owns
  // onboarding calls and installation handover, not the mockup step, and a
  // technician has no reason to see it either.
  const canSeeMockup = [
    "admin",
    "product_specialist",
    "growth_specialist",
    "dealer",
    "sales_lead",
    "accounts",
    "ops_associate",
    "ops_lead",
  ].some((role) => userRoles.includes(role));
  // Mirrors ORDERS_BALANCE_GATE_ROLES on the backend — only these roles get
  // even the restricted "gate state only" balance view. Any other restricted
  // role (engagement, technician) gets advancePayment.data === null, so this
  // must gate the card too or it renders a misleading "Blocked/₹0" card off
  // of all-undefined fields instead of just not showing at all.
  const canSeeBalanceGate = [
    "admin",
    "support",
    "product_specialist",
    "growth_specialist",
    "dealer",
    "sales_lead",
    "accounts",
    "ops_associate",
  ].some((role) => userRoles.includes(role));
  // Fulfilment fields (serial, AWB, etc.) are otherwise one-shot, set once via
  // the OA6/OA9 queue forms with no way to fix a typo — admin, ops associate,
  // and ops lead all get an override to correct them afterwards. Mirrors
  // dispatchEditAccessRoles on the backend (admin-panel.js) — the API
  // already allowed these two roles, only this button was admin-only.
  const canEditFulfilment = ["admin", "ops_associate", "ops_lead"].some((role) =>
    userRoles.includes(role),
  );

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mockupPreview, setMockupPreview] = useState(null);

  const [advancePayment, setAdvancePayment] = useState(null);
  const [paymentRestricted, setPaymentRestricted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [pricingChecked, setPricingChecked] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [submittingCredentialsOnly, setSubmittingCredentialsOnly] = useState(false);
  const [editQuoteOpen, setEditQuoteOpen] = useState(false);
  const [editInvoiceOpen, setEditInvoiceOpen] = useState(false);
  const [resendingLink, setResendingLink] = useState(false);
  const [resendingBalanceLink, setResendingBalanceLink] = useState(false);
  const [savingAdvanceDueDate, setSavingAdvanceDueDate] = useState(false);
  const [savingBalanceDueDate, setSavingBalanceDueDate] = useState(false);
  const [advanceResendDueDate, setAdvanceResendDueDate] = useState("");
  const [balanceResendDueDate, setBalanceResendDueDate] = useState("");
  const [cancellingAdvanceLink, setCancellingAdvanceLink] = useState(false);
  const [cancellingBalanceLink, setCancellingBalanceLink] = useState(false);
  const [updatingQuoteAccepted, setUpdatingQuoteAccepted] = useState(false);
  const [sendingAgreement, setSendingAgreement] = useState(false);
  const [updatingAgreementSigned, setUpdatingAgreementSigned] = useState(false);
  const [purchaseAgreementFile, setPurchaseAgreementFile] = useState(null);
  const [uploadingPurchaseAgreement, setUploadingPurchaseAgreement] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [checklistPhotos, setChecklistPhotos] = useState(null);
  const [loadingChecklistPhotos, setLoadingChecklistPhotos] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [editFulfilmentOpen, setEditFulfilmentOpen] = useState(false);
  const [testCallSlot, setTestCallSlot] = useState("");
  const [submittingTestCallSlot, setSubmittingTestCallSlot] = useState(false);
  const [testLogoFile, setTestLogoFile] = useState(null);
  const [submittingTestLogo, setSubmittingTestLogo] = useState(false);
  const [emailingInvoice, setEmailingInvoice] = useState(false);
  const [refreshingInvoice, setRefreshingInvoice] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({
    amount: "",
    dueDate: "",
    paymentMode: "razorpay",
    skipReceipt: false,
    alreadyRecordedInZoho: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [advanceConfirmStep, setAdvanceConfirmStep] = useState(false);

  // EMI status state
  const [emiRejectDialogOpen, setEmiRejectDialogOpen] = useState(false);
  const [emiRejectReason, setEmiRejectReason] = useState("");
  const [updatingEmiStatus, setUpdatingEmiStatus] = useState(false);
  const [updatingPaymentType, setUpdatingPaymentType] = useState(false);

  // EMI revise modal state
  const [reviseEmiOpen, setReviseEmiOpen] = useState(false);

  // `restricted` is a sibling of `data` in the response, not a field on the
  // order itself — attach it so canSee() below can actually see it. Action
  // endpoints (quote/agreement/EMI updates) return the order alone with no
  // fresh `restricted`, so on those we keep whatever we last knew.
  const applyOrderResponse = (orderData, restrictedGroups) => {
    setOrder((prev) => ({
      ...orderData,
      restricted: restrictedGroups !== undefined ? restrictedGroups : prev?.restricted || [],
    }));
  };

  const fetchOrder = async () => {
    try {
      const res = await apiService.getOrder(id, token);
      applyOrderResponse(res.data, res.restricted);
    } catch (error) {
      toast.error("Failed to load order");
    } finally {
      setLoading(false);
    }
  };
  const fetchAdvancePayment = async () => {
    try {
      const res = await apiService.getAdvancePayment(id, token);
      setAdvancePayment(res.data);
      setPaymentRestricted(Boolean(res.restricted));
    } catch (error) {
      setAdvancePayment(null);
      setPaymentRestricted(false);
    }
  };
  const fetchMockupPreview = async () => {
    try {
      const res = await apiService.fetchMockupPreview(id, token);
      setMockupPreview(res.data);
    } catch (error) {
      setMockupPreview(null);
    }
  };
  useEffect(() => {
    fetchOrder();
    fetchAdvancePayment();
    fetchMockupPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pageSx = {
    padding: { xs: "18px 16px 50px", md: "24px 28px 60px" },
    maxWidth: 1240,
  };

  if (loading) {
    return <Loader />;
  }

  if (!order) {
    return (
      <Box sx={pageSx}>
        <BackLink onClick={() => navigate("/dashboard/orders")}>
          ‹ Back to orders
        </BackLink>
        <Box sx={{ fontSize: 13, color: UI.ink3 }}>Order not found.</Box>
      </Box>
    );
  }

  /* The backend only has one status value ("agreement") for the entire
     pre-onboarding span — draft, quotation sent, advance paid, agreement
     signed all look identical to it. The header badge used to show the raw
     "AGREEMENT" label even for a brand-new draft order. Use the same
     granular stage the stepper below already computes so the badge and the
     stepper never disagree. */
  const statusMeta =
    order.status === "agreement"
      ? { label: computeCurrentStage(order).toUpperCase(), color: "info" }
      : STATUS_META[order.status] || { label: order.status, color: "default" };





  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date.toISOString().split("T")[0];
  };

  // Native <input type="date"> needs YYYY-MM-DD — stored values come back as
  // full ISO datetimes.
  const toDateInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
  };

  const openAdvanceModal = () => {
    setAdvanceForm({
      amount: "",
      dueDate: getDefaultDueDate(),
      paymentMode: "razorpay",
      skipReceipt: false,
      alreadyRecordedInZoho: false,
    });
    setAdvanceConfirmStep(false);
    setModalOpen(true);
  };

  const closeAdvanceModal = () => {
    setModalOpen(false);
    setAdvanceConfirmStep(false);
  };

  // Validates the form and, if it checks out, shows a summary (price/mode/
  // email) for the user to confirm before anything actually gets created —
  // a Razorpay link or a recorded payment are both hard to walk back once
  // sent/saved, so a one-line "Continue" shouldn't be the only gate.
  const handleReviewAdvanceLink = () => {
    const amountValue = Number(advanceForm.amount);

    if (!amountValue || amountValue <= 0) {
      toast.error("Enter a valid advance amount");
      return;
    }

    const isBankTransfer = advanceForm.paymentMode === "bank_transfer";

    if (!isBankTransfer && !advanceForm.dueDate) {
      toast.error("Due date is required");
      return;
    }

    setAdvanceConfirmStep(true);
  };

  const handleCreateAdvanceLink = async () => {
    const amountValue = Number(advanceForm.amount);
    const isBankTransfer = advanceForm.paymentMode === "bank_transfer";

    setSubmitting(true);
    try {
      const res = isBankTransfer
        ? await apiService.recordManualAdvancePayment(
            id,
            {
              amount: amountValue,
              skipReceipt: advanceForm.skipReceipt,
              alreadyRecordedInZoho: advanceForm.alreadyRecordedInZoho,
            },
            token,
          )
        : await apiService.createAdvancePaymentLink(
            id,
            { amount: amountValue, dueDate: advanceForm.dueDate },
            token,
          );
      setAdvancePayment(res.data);
      toast.success(
        isBankTransfer
          ? "Advance payment recorded as received"
          : "Advance payment link created",
      );
      setModalOpen(false);
      setAdvanceConfirmStep(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (isBankTransfer
            ? "Failed to record advance payment"
            : "Failed to create advance payment link"),
      );
    } finally {
      setSubmitting(false);
    }
  };
  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  // Admin-only correction for a wrongly-entered payment (e.g. a duplicate
  // manual entry of one already paid online). Backend restricts this to the
  // most recent entry and blocks it once the order is fully paid.
  const handleVoidPayment = async (paymentEntryId) => {
    const reason = window.prompt(
      "Reason for voiding this payment (required — this is logged on the order):",
    );
    if (!reason || !reason.trim()) return;

    setSubmitting(true);
    try {
      const res = await apiService.voidAdvancePayment(
        id,
        { paymentEntryId, reason },
        token,
      );
      setAdvancePayment(res.data);
      toast.success("Payment voided");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to void payment",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendAdvanceLink = async () => {
    setResendingLink(true);
    try {
      await apiService.resendAdvanceLink(id, token, advanceResendDueDate || undefined);
      toast.success(advanceResendDueDate ? "Payment link resent, due date updated" : "Payment link resent to customer");
      setAdvanceResendDueDate("");
      fetchAdvancePayment();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to resend payment link",
      );
    } finally {
      setResendingLink(false);
    }
  };

  // Updates the due date without pinging the customer again — same link,
  // same URL, just a corrected expiry (see handleResendAdvanceLink for the
  // notify=true version that also resends SMS/email).
  const handleSaveAdvanceDueDate = async () => {
    if (!advanceResendDueDate) return;
    setSavingAdvanceDueDate(true);
    try {
      await apiService.resendAdvanceLink(id, token, advanceResendDueDate, false);
      toast.success("Due date updated");
      setAdvanceResendDueDate("");
      fetchAdvancePayment();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update due date",
      );
    } finally {
      setSavingAdvanceDueDate(false);
    }
  };

  const handleResendBalanceLink = async () => {
    setResendingBalanceLink(true);
    try {
      await apiService.resendBalanceLink(id, token, balanceResendDueDate || undefined);
      toast.success(balanceResendDueDate ? "Balance link resent, due date updated" : "Balance payment link resent to customer");
      setBalanceResendDueDate("");
      fetchOrder();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to resend balance payment link",
      );
    } finally {
      setResendingBalanceLink(false);
    }
  };

  const handleSaveBalanceDueDate = async () => {
    if (!balanceResendDueDate) return;
    setSavingBalanceDueDate(true);
    try {
      await apiService.resendBalanceLink(id, token, balanceResendDueDate, false);
      toast.success("Due date updated");
      setBalanceResendDueDate("");
      fetchOrder();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update due date",
      );
    } finally {
      setSavingBalanceDueDate(false);
    }
  };

  const handleCancelAdvanceLink = async () => {
    if (!window.confirm("Cancel this advance payment link? It will no longer be payable.")) {
      return;
    }
    setCancellingAdvanceLink(true);
    try {
      await apiService.cancelPaymentLink(id, "advance", token);
      toast.success("Advance payment link cancelled");
      fetchAdvancePayment();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel payment link");
    } finally {
      setCancellingAdvanceLink(false);
    }
  };

  const handleCancelBalanceLink = async () => {
    if (!window.confirm("Cancel this balance payment link? It will no longer be payable.")) {
      return;
    }
    setCancellingBalanceLink(true);
    try {
      await apiService.cancelPaymentLink(id, "balance", token);
      toast.success("Balance payment link cancelled");
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel balance payment link");
    } finally {
      setCancellingBalanceLink(false);
    }
  };

  const handleSetQuoteAccepted = async (accepted) => {
    setUpdatingQuoteAccepted(true);
    try {
      const res = await apiService.setQuoteAccepted(id, accepted, token);
      applyOrderResponse(res.data);
      toast.success(accepted ? "Quote marked as accepted" : "Quote marked as not accepted");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to record quote acceptance",
      );
    } finally {
      setUpdatingQuoteAccepted(false);
    }
  };

  const handleSendPurchaseAgreement = async () => {
    setSendingAgreement(true);
    try {
      const res = await apiService.sendPurchaseAgreement(id, token);
      applyOrderResponse(res.data);
      toast.success("Purchase agreement marked as sent");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to mark the agreement as sent");
    } finally {
      setSendingAgreement(false);
    }
  };

  const handleSetAgreementSigned = async (signed) => {
    setUpdatingAgreementSigned(true);
    try {
      const res = await apiService.setAgreementSigned(id, signed, token);
      applyOrderResponse(res.data);
      toast.success(signed ? "Agreement marked as signed" : "Agreement marked as not signed");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to record agreement signature",
      );
    } finally {
      setUpdatingAgreementSigned(false);
    }
  };

  const handleUploadPurchaseAgreement = async () => {
    if (!purchaseAgreementFile) {
      toast.error("Choose a PDF file first");
      return;
    }
    setUploadingPurchaseAgreement(true);
    try {
      const res = await apiService.uploadPurchaseAgreement(id, purchaseAgreementFile, token);
      applyOrderResponse(res.data);
      setPurchaseAgreementFile(null);
      toast.success("Purchase agreement PDF uploaded");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to upload the purchase agreement PDF",
      );
    } finally {
      setUploadingPurchaseAgreement(false);
    }
  };

  const handleUploadInvoice = async () => {
    if (!invoiceFile) {
      toast.error("Choose a PDF file first");
      return;
    }
    setUploadingInvoice(true);
    try {
      const res = await apiService.uploadInvoice(id, invoiceFile, token);
      applyOrderResponse(res.data);
      setInvoiceFile(null);
      toast.success("Invoice PDF uploaded");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to upload the invoice PDF",
      );
    } finally {
      setUploadingInvoice(false);
    }
  };

  const handleOpenPurchaseAgreement = async () => {
    try {
      const res = await apiService.downloadPurchaseAgreement(id, token);
      window.open(res.data.url, "_blank");
    } catch (error) {
      toast.error("Failed to open purchase agreement PDF");
    }
  };

  const handleLoadChecklistPhotos = async () => {
    setLoadingChecklistPhotos(true);
    try {
      const res = await apiService.fetchChecklistPhotos(id, token);
      setChecklistPhotos(res.data || []);
    } catch (error) {
      toast.error("Failed to load site photos");
    } finally {
      setLoadingChecklistPhotos(false);
    }
  };

  const handleTestSelectCallSlot = async () => {
    if (!testCallSlot) {
      toast.error("Pick a date and time first");
      return;
    }
    setSubmittingTestCallSlot(true);
    try {
      const res = await apiService.testSelectOnboardingCallSlot(
        id,
        new Date(testCallSlot).toISOString(),
        token,
      );
      applyOrderResponse(res.data);
      setTestCallSlot("");
      toast.success(" Slot selected as the customer");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to select the slot",
      );
    } finally {
      setSubmittingTestCallSlot(false);
    }
  };

  const handleTestUploadLogo = async () => {
    if (!testLogoFile) {
      toast.error("Choose a logo file first");
      return;
    }
    setSubmittingTestLogo(true);
    try {
      const res = await apiService.testUploadClinicLogo(id, testLogoFile, token);
      applyOrderResponse(res.data);
      setTestLogoFile(null);
      toast.success("Clinic logo uploaded as the customer");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to upload the logo",
      );
    } finally {
      setSubmittingTestLogo(false);
    }
  };


  const openQuoteModal = () => {
    setPricingChecked(false);
    setQuoteModalOpen(true);
  };

  const handleSendQuotation = async () => {
    if (!pricingChecked) {
      toast.error("Confirm pricing, discount and GST before sending");
      return;
    }
    setSubmittingQuote(true);
    try {
      const res = await apiService.sendQuotation(id, "", token);
      applyOrderResponse(res.data);
      toast.success("Quotation sent to doctor");
      setQuoteModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send quotation");
    } finally {
      setSubmittingQuote(false);
    }
  };
  const handleSendCredentialsOnly = async () => {
    setSubmittingCredentialsOnly(true);
    try {
      const res = await apiService.sendEngageCredentialsAndMarkAccepted(id, "", token);
      applyOrderResponse(res.data);
      toast.success("Engage credentials emailed — quotation marked accepted");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to send Engage credentials",
      );
    } finally {
      setSubmittingCredentialsOnly(false);
    }
  };
  const handleOpenQuote = async () => {
    try {
      const res = await apiService.downloadQuote(id, token);
      window.open(res.data.url, "_blank");
    } catch (error) {
      toast.error("Failed to open quote PDF");
    }
  };

  const handleOpenInvoice = async () => {
    try {
      const res = await apiService.downloadInvoice(id, token);
      window.open(res.data.url, "_blank");
    } catch (error) {
      toast.error("Failed to open invoice PDF");
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const res = await apiService.downloadInvoice(id, token, true);
      window.open(res.data.url, "_blank");
    } catch (error) {
      toast.error("Failed to download invoice PDF");
    }
  };

  const handleEmailInvoice = async () => {
    setEmailingInvoice(true);
    try {
      const res = await apiService.emailInvoiceToDoctor(id, token);
      applyOrderResponse(res.data);
      toast.success("Invoice emailed to doctor");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to email invoice");
    } finally {
      setEmailingInvoice(false);
    }
  };

  const handleRefreshInvoice = async () => {
    setRefreshingInvoice(true);
    try {
      const res = await apiService.refreshInvoice(id, token);
      applyOrderResponse(res.data);
      toast.success("Invoice refreshed");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to refresh invoice");
    } finally {
      setRefreshingInvoice(false);
    }
  };

  const handleOpenMockup = async () => {
    try {
      const res = await apiService.fetchMockupPreview(id, token);
      if (!res.data?.logoUrl) throw new Error("No logo file");
      window.open(res.data.logoUrl, "_blank");
    } catch (error) {
      toast.error("Failed to open clinic logo");
    }
  };

  const handleDownloadMockup = async () => {
    try {
      const res = await apiService.fetchMockupPreview(id, token, true);
      if (!res.data?.logoUrl) throw new Error("No logo file");
      window.open(res.data.logoUrl, "_blank");
    } catch (error) {
      toast.error("Failed to download clinic logo");
    }
  };

  const handleDownloadQuote = async () => {
    try {
      const res = await apiService.downloadQuote(id, token, true);
      window.open(res.data.url, "_blank");
    } catch (error) {
      toast.error("Failed to download quote PDF");
    }
  };

  // EMI status handlers
  const handleUpdateEmiStatus = async (newStatus, reason) => {
    setUpdatingEmiStatus(true);
    try {
      const res = await apiService.updateEmiStatus(
        id,
        newStatus,
        reason,
        token,
      );
      applyOrderResponse(res.data);
      toast.success(
        newStatus === "documents_pending"
          ? "Emailed Massfintech with the quote PDF"
          : "EMI status updated",
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update EMI status",
      );
    } finally {
      setUpdatingEmiStatus(false);
    }
  };

  // Quick payment-type toggle right on this page — deliberately separate
  // from Edit Quote, which resets the quote PDF's sent/signed flags and
  // re-issues the Zoho estimate. This just flips the one field.
  const handleTogglePaymentType = async () => {
    const nextType = order.quote?.paymentType === "emi" ? "advance" : "emi";
    setUpdatingPaymentType(true);
    try {
      const res = await apiService.updateOrderPaymentType(id, nextType, token);
      applyOrderResponse(res.data);
      toast.success(`Payment type switched to ${nextType.toUpperCase()}`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update payment type",
      );
    } finally {
      setUpdatingPaymentType(false);
    }
  };

  const openEmiRejectDialog = () => {
    setEmiRejectReason("");
    setEmiRejectDialogOpen(true);
  };

  const handleConfirmEmiReject = async () => {
    await handleUpdateEmiStatus("rejected", emiRejectReason);
    setEmiRejectDialogOpen(false);
  };

  /* The server withholds field groups this role may not see and names them
     in `restricted`. We only explain the gap; we never do the hiding. */
  const restricted = order.restricted || [];
  const canSee = (group) => !restricted.includes(group);



  // The most recent advance entry, not just the first — an order can take
  // several advance/down-payment installments (createAdvancePaymentLink
  // numbers them), and gating "Create advance payment link" on whether the
  // *first* one is paid hid the option entirely after that, even with a
  // large balance still outstanding. Abandoned entries (Razorpay no longer
  // has the link — see reconciliationAbandonedAt) are skipped here too, so
  // a dead link shows "Create a new link" instead of dead Copy/Resend actions.
  const latestAdvanceEntry = advancePayment?.paymentHistory
    ?.filter((entry) => entry.type === "advance" && !entry.reconciliationAbandonedAt)
    .slice(-1)[0];
  const balanceEntry = advancePayment?.paymentHistory
    ?.slice()
    .reverse()
    .find((entry) => entry.type === "balance" && !entry.reconciliationAbandonedAt);

  /* The Payments card needs every entry, not just one advance + one balance —
     an order can have several advance installments (createAdvancePaymentLink
     numbers them), and showing only the first one hides real paid amounts. */
  const paymentRows = (() => {
    const history = advancePayment?.paymentHistory || [];
    const byType = { advance: [], balance: [] };
    // A cancelled link (reconciliationAbandonedAt) is no longer a real
    // commitment — it shouldn't keep showing as "due" here alongside
    // whatever active entry replaced it.
    history.forEach((entry) => {
      if (entry.reconciliationAbandonedAt) return;
      byType[entry.type]?.push(entry);
    });

    const rowsForType = (type, dueLabel) => {
      const entries = byType[type];
      if (!entries.length) {
        return [{ key: `${type}-none`, label: `${type === "advance" ? "Advance" : "Balance"}, ${dueLabel}`, amount: 0 }];
      }
      return entries.map((entry, i) => ({
        key: entry._id || `${type}-${i}`,
        label: `${type === "advance" ? "Advance" : "Balance"}${entries.length > 1 ? ` ${i + 1}` : ""}${
          entry.status === "paid"
            ? `, received ${new Date(entry.paidAt).toLocaleDateString()}${
                entry.method === "massfintech_financing" ? " (via EMI)" : ""
              }`
            : `, ${dueLabel}`
        }`,
        amount: entry.amount,
      }));
    };

    // EMI orders don't have a "balance due on dispatch" concept — the rest is
    // financed by Massfintech, not billed through a balance link — so only
    // show that placeholder row on the advance-then-balance path. A real
    // balance entry (shouldn't normally exist for EMI) still shows if present.
    const showBalance = order.quote?.paymentType === "advance" || byType.balance.length > 0;

    return [...rowsForType("advance", "due"), ...(showBalance ? rowsForType("balance", "due on dispatch") : [])];
  })();

  // advancePayment.outstandingBalance only ever drops once a payment
  // actually clears — correct for gating dispatch, but for an EMI order a
  // down payment link being sent is already a commitment against the total,
  // so what should get financed (and shown here) is total minus every
  // advance entry regardless of paid status, not just the paid ones.
  const displayOutstanding =
    order.quote?.paymentType === "emi"
      ? Math.max(
          (order.quote?.totalAmount || 0) -
            (advancePayment?.paymentHistory || [])
              // Same exclusion as paymentRows — a cancelled link is no
              // longer a real commitment against the total being financed.
              .filter((entry) => entry.type === "advance" && !entry.reconciliationAbandonedAt)
              .reduce((sum, entry) => sum + entry.amount, 0) -
            // Once the balance has actually been resolved — paid directly,
            // or recorded as financed by Massfintech via
            // recordEmiBalancePayment — it's no longer outstanding against
            // the total, even though it isn't an "advance" entry. Without
            // this, a fully-financed EMI order keeps showing its
            // pre-financing remainder here forever.
            (advancePayment?.paymentHistory || [])
              .filter((entry) => entry.type === "balance" && entry.status === "paid")
              .reduce((sum, entry) => sum + entry.amount, 0),
          0,
        )
      : advancePayment?.outstandingBalance;

  const paymentClearedForAgreement =
    order.quote?.paymentType === "emi"
      ? // Unlocks once the lead's been emailed to Massfintech (documents_pending
        // or later) — not full approval, so the agreement/mockup/onboarding
        // flow doesn't sit blocked for however long Massfintech takes to decide.
        Boolean(order.emi?.status) && order.emi.status !== "not_started"
      : latestAdvanceEntry?.status === "paid";
  return (
    <Box sx={pageSx}>
      <BackLink onClick={() => navigate(-1)}>‹ Back</BackLink>

      <Card sx={{ marginBottom: "20px", padding: "18px 20px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "12px",
            flexWrap: "wrap",
            marginBottom: "16px",
          }}
        >
          <Box sx={{ fontSize: 18, fontWeight: 500, color: UI.ink }}>
            {order.clinic?.name || "—"}
          </Box>
          <Tag tone={TONES[statusMeta.color] || "i"}>{statusMeta.label}</Tag>
          {order.hold?.held && <Tag tone="b">Held</Tag>}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
            gap: "16px",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11, color: UI.ink3, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Doctor
            </Box>
            <Box sx={{ fontSize: 14, marginTop: "2px", overflowWrap: "anywhere" }}>{order.doctor?.name || "—"}</Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11, color: UI.ink3, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              City
            </Box>
            <Box sx={{ fontSize: 14, marginTop: "2px", overflowWrap: "anywhere" }}>{order.clinic?.state || "—"}</Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11, color: UI.ink3, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Owner
            </Box>
            <Box sx={{ fontSize: 14, marginTop: "2px", overflowWrap: "anywhere" }}>{order.ownerId?.name || "—"}</Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11, color: UI.ink3, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Waiting on
            </Box>
            <Box sx={{ fontSize: 14, marginTop: "2px", overflowWrap: "anywhere" }}>{computeWaitingOn(order)}</Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11, color: UI.ink3, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Value
            </Box>
            <Box sx={{ fontSize: 14, marginTop: "2px", overflowWrap: "anywhere" }}>
              {canSee("commercials") || advancePayment
                ? rupee(order.quote?.totalAmount)
                : "Restricted"}
            </Box>
          </Box>
        </Box>
        <Stepper order={order} advancePaid={latestAdvanceEntry?.status === "paid"} />
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 320px" },
          gap: "18px",
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "18px",
          }}
        >
          {canSee("commercials") && (
          <Card>
            <CardHeader title="Commercials" />
            <Row
              label="Product"
              value={
                PRODUCT_LABELS[order.quote?.product] ||
                order.quote?.product ||
                "-"
              }
            />
            <Row
              label="Owner"
              value={`${order.ownerId?.name || "—"} · ${
                order.ownerId?.role?.includes("dealer") ? "DEALER" : "INTERNAL"
              }`}
            />
            <Row
              label="Price (incl. taxes)"
              value={rupee(order.quote?.totalAmount)}
            />
            <Box
              sx={{
                padding: "9px 0",
                borderBottom: "1px solid",
                borderBottomColor: UI.line,
              }}
            >
              <Box sx={{ fontSize: 13, color: UI.ink3, marginBottom: "3px" }}>
                Payment term
              </Box>
              <Box
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: UI.ink,
                  whiteSpace: "pre-wrap",
                }}
              >
                {order.quote?.paymentTerm}
              </Box>
            </Box>
            <Row
              label="Quote accepted"
              value={
                <Tag tone={order.quotePdf?.signed ? "g" : "i"}>
                  {order.quotePdf?.signed ? "YES" : "NO"}
                </Tag>
              }
              last
            />
          </Card>
          )}
          {!canSee("commercials") && <Restrict what="Commercial detail" />}
          {canSee("commercials") && (
          <Card>
            <CardHeader
              title="Quotation"
              tag={
                <Tag
                  tone={
                    order.quotePdf?.signed
                      ? "g"
                      : order.quotePdf?.sent
                        ? "s"
                        : "i"
                  }
                >
                  {order.quotePdf?.signed
                    ? "ACCEPTED"
                    : order.quotePdf?.sent
                      ? "SENT, NO RESPONSE"
                      : "PENDING REVIEW"}
                </Tag>
              }
            />
            {!order.quotePdf?.sent ? (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{ fontSize: 13, color: UI.ink3, marginBottom: "14px" }}
                >
                  Review the quotation before sending it to the doctor.
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                  <Btn
                    text="Review and send quotation"
                    variant="ghost"
                    onClick={openQuoteModal}
                    width="260px"
                    sx={{ textTransform: "none" }}
                  />
                  <Btn
                    text="Edit quote"
                    onClick={() => setEditQuoteOpen(true)}
                    width="140px"
                    sx={{ textTransform: "none" }}
                  />
                  <Btn
                    text="Send credentials only (skip quote)"
                    variant="ghost"
                    disabled={submittingCredentialsOnly}
                    onClick={handleSendCredentialsOnly}
                    width="280px"
                    sx={{ textTransform: "none" }}
                  />
                </Box>
                <Box sx={{ fontSize: 12, color: UI.ink3, mb: 1 }}>
                  For leads already finalized outside the normal review flow
                  — emails only the Engage trial credentials, skips the quote
                  PDF/review email, and marks the quotation accepted.
                </Box>
                <QuotePdfRow
                  onOpen={handleOpenQuote}
                  onDownload={handleDownloadQuote}
                />
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                  {order.quotePdf?.signed
                    ? "Customer accepted the quotation."
                    : "Quotation emailed to the doctor. Awaiting response."}
                </Box>
                <Box sx={{ display: "flex", gap: 2, mt: 1.5, mb: 2, flexWrap: "wrap" }}>
                  <Btn
                    text="Edit quote"
                    onClick={() => setEditQuoteOpen(true)}
                    width="140px"
                    sx={{ textTransform: "none" }}
                  />
                  {order.quotePdf?.signed ? (
                    <Btn
                      text="Mark not accepted"
                      variant="ghost"
                      onClick={() => handleSetQuoteAccepted(false)}
                      width="180px"
                      sx={{ textTransform: "none" }}
                      disabled={updatingQuoteAccepted}
                    />
                  ) : (
                    <Btn
                      text="Mark accepted"
                      variant="ghost"
                      onClick={() => handleSetQuoteAccepted(true)}
                      width="160px"
                      sx={{ textTransform: "none" }}
                      disabled={updatingQuoteAccepted}
                    />
                  )}
                </Box>
                <QuotePdfRow
                  onOpen={handleOpenQuote}
                  onDownload={handleDownloadQuote}
                />
              </Box>
            )}
          </Card>
          )}

          {!canSee("payments") && <Restrict what="Payment detail" />}

          {(() => {
            const rawHistory = advancePayment?.paymentHistory || [];
            const mostRecentEntry = rawHistory[rawHistory.length - 1];
            const canVoidPayments =
              isAdmin &&
              advancePayment?.paymentStatus !== "paid_fully" &&
              mostRecentEntry?.status === "paid";

            return (
              canSee("payments") &&
              ["advance", "emi"].includes(order.quote?.paymentType) && (
                <Card>
                  <CardHeader title="Payments" />
                  <Row label="Order total" value={rupee(order.quote?.totalAmount)} />
                  {paymentRows.map((row) => (
                    <Row
                      key={row.key}
                      label={row.label}
                      value={
                        canVoidPayments && row.key === mostRecentEntry._id ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {rupee(row.amount)}
                            <Box
                              component="button"
                              type="button"
                              onClick={() => handleVoidPayment(row.key)}
                              disabled={submitting}
                              sx={{
                                border: 0,
                                bgcolor: "transparent",
                                padding: 0,
                                color: UI.red,
                                fontSize: 11,
                                fontWeight: 500,
                                cursor: submitting ? "default" : "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              Void
                            </Box>
                          </Box>
                        ) : (
                          rupee(row.amount)
                        )
                      }
                    />
                  ))}
                  <Row
                    label={<Box sx={{ fontWeight: 700, color: UI.ink }}>Outstanding</Box>}
                    value={
                      <Box sx={{ fontWeight: 700 }}>
                        {rupee(displayOutstanding)}
                      </Box>
                    }
                    last
                  />
                </Card>
              )
            );
          })()}

          {canSee("payments") &&
            order.quotePdf?.signed &&
            ["advance", "emi"].includes(order.quote?.paymentType) &&
            // displayOutstanding, not advancePayment.outstandingBalance —
            // that raw field also counts balance-type payments, so an EMI
            // order with an (unusual, but real) balance entry paid could
            // hit zero there and hide this card even though the real
            // EMI-financed amount still needs a down payment collected.
            (!advancePayment || displayOutstanding > 0) && (
            <Card>
              <CardHeader
                title={latestAdvanceEntry ? "Payment" : "Advance payment"}
                tag={
                  latestAdvanceEntry && (
                    <Tag
                      tone={
                        latestAdvanceEntry.status === "link_created"
                          ? "s"
                          : "i"
                      }
                    >
                      {latestAdvanceEntry.status === "link_created"
                        ? "UNPAID"
                        : "NOT REQUESTED"}
                    </Tag>
                  )
                }
              />

              {latestAdvanceEntry?.status === "link_created" ? (
                <React.Fragment>
                  <Row label="Amount" value={rupee(latestAdvanceEntry.amount)} />
                  <Row
                    label="Due date"
                    value={
                      latestAdvanceEntry.dueDate
                        ? new Date(latestAdvanceEntry.dueDate).toLocaleDateString()
                        : "—"
                    }
                  />
                  <Box
                    sx={{ fontSize: 11, color: UI.ink3, marginTop: "12px" }}
                  >
                    {latestAdvanceEntry.createdAt
                      ? `Link sent ${Math.max(
                          Math.floor(
                            (Date.now() -
                              new Date(latestAdvanceEntry.createdAt).getTime()) /
                              (1000 * 60 * 60 * 24),
                          ),
                          0,
                        )} days ago, not paid.`
                      : "Not paid yet."}
                  </Box>
                  <Box
                    sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    <Btn
                      text="Copy link"
                      onClick={() =>
                        handleCopyLink(latestAdvanceEntry.razorpayLinkUrl)
                      }
                      width="140px"
                      sx={{ textTransform: "none" }}
                    />
                    <Btn
                      text="Open link"
                      onClick={() =>
                        window.open(latestAdvanceEntry.razorpayLinkUrl, "_blank")
                      }
                      width="140px"
                      sx={{ textTransform: "none" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid",
                      borderTopColor: UI.line,
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      label="New due date (optional)"
                      type="date"
                      value={advanceResendDueDate}
                      onChange={(e) => setAdvanceResendDueDate(e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 190 }}
                    />
                    <Tooltip
                      title={advanceResendDueDate ? "Save this due date without resending the link" : "Pick a new due date first"}
                      arrow
                    >
                      <span>
                        <Btn
                          text="Save"
                          variant={advanceResendDueDate ? "solid" : "ghost"}
                          onClick={handleSaveAdvanceDueDate}
                          width="90px"
                          sx={{ textTransform: "none" }}
                          disabled={savingAdvanceDueDate || !advanceResendDueDate}
                        />
                      </span>
                    </Tooltip>
                    <Btn
                      text="Resend link"
                      onClick={handleResendAdvanceLink}
                      width="140px"
                      sx={{ textTransform: "none" }}
                      disabled={resendingLink}
                    />
                    <Btn
                      text="Cancel link"
                      variant="ghost"
                      onClick={handleCancelAdvanceLink}
                      width="140px"
                      sx={{ textTransform: "none", color: UI.danger || "#c62828" }}
                      disabled={cancellingAdvanceLink}
                    />
                  </Box>

                  {/* An unpaid link existing shouldn't block asking for a
                      SEPARATE payment too — e.g. a second down-payment
                      installment. Backend already numbers multiple advance
                      entries (createAdvancePaymentLink); this was just never
                      exposed once one link already existed. */}
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid",
                      borderTopColor: UI.line,
                    }}
                  >
                    <Box sx={{ fontSize: 11, color: UI.ink3, marginBottom: "8px" }}>
                      Need to collect a separate payment on top of this one?
                    </Box>
                    <Btn
                      text="Create another payment link"
                      variant="ghost"
                      onClick={openAdvanceModal}
                      width="220px"
                      sx={{ textTransform: "none" }}
                    />
                  </Box>
                </React.Fragment>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Box
                    sx={{ fontSize: 13, color: UI.ink3, marginBottom: "14px" }}
                  >
                    {latestAdvanceEntry
                      ? "Balance still outstanding — another payment link can be requested."
                      : "No advance payment link created yet."}
                  </Box>
                  <Btn
                    text={latestAdvanceEntry ? "Create payment link" : "Create advance payment link"}
                    variant="ghost"
                    onClick={openAdvanceModal}
                    width="240px"
                    sx={{ textTransform: "none" }}
                  />
                </Box>
              )}
            </Card>
          )}

          {canSee("payments") && order.quotePdf?.signed && (
            <Card>
              {order.quote?.paymentType === "emi" ? (
                <CardHeader
                  title="EMI status"
                  tag={
                    <Tag
                      tone={
                        order.emi?.status === "approved"
                          ? "g"
                          : order.emi?.status === "rejected"
                            ? "b"
                            : order.emi?.status === "under_review"
                              ? "s"
                              : "i"
                      }
                    >
                      {(order.emi?.status || "not_started")
                        .replace(/_/g, " ")
                        .toUpperCase()}
                    </Tag>
                  }
                />
              ) : (
                <CardHeader title="Payment options" />
              )}

              {order.quote?.paymentType !== "emi" && (
                <Btn
                  text="This customer wants EMI instead"
                  variant="ghost"
                  onClick={handleTogglePaymentType}
                  width="260px"
                  sx={{ textTransform: "none", mb: 1.5 }}
                  disabled={updatingPaymentType}
                />
              )}

              {order.quote?.paymentType !== "emi" ? (
                <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                  This order is currently on Advance payment. If the
                  customer asks for EMI instead, use the button above — no
                  need to edit or resend the quote.
                </Box>
              ) : (
                <>
              {order.emi?.status === "rejected" && (
                <>
                  {order.emi?.rejectionReason && (
                    <Box
                      sx={{
                        padding: "10px 13px",
                        borderRadius: "4px",
                        bgcolor: UI.redBg,
                        border: "1px solid",
                        borderColor: UI.red,
                        color: UI.redInk,
                        fontSize: 12,
                        marginBottom: "14px",
                      }}
                    >
                      Rejected: {order.emi.rejectionReason}
                    </Box>
                  )}
                  <Btn
                    text="Create revised payment terms"
                    variant="ghost"
                    onClick={() => setReviseEmiOpen(true)}
                    width="260px"
                    sx={{ textTransform: "none", mb: 1 }}
                  />
                </>
              )}

              {order.emi?.revisedTermsStatus &&
                order.emi.revisedTermsStatus !== "not_sent" && (
                  <Box sx={{ mt: 1.5, mb: 1.5 }}>
                    <Box sx={{ fontSize: 11, color: UI.ink3 }}>
                      Customer response to revised terms
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      <Tag
                        tone={
                          order.emi.revisedTermsStatus === "accepted"
                            ? "g"
                            : order.emi.revisedTermsStatus === "declined"
                              ? "b"
                              : "i"
                        }
                      >
                        {order.emi.revisedTermsStatus.toUpperCase()}
                      </Tag>
                    </Box>
                  </Box>
                )}

              {order.emi?.status === "approved" ? (
                <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                  EMI approved by Massfintech.
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                  {order.emi?.status !== "documents_pending" &&
                    !(
                      order.emi?.status === "rejected" &&
                      order.emi?.revisedTermsStatus &&
                      order.emi.revisedTermsStatus !== "not_sent"
                    ) && (
                    <Btn
                      text="Email Massfintech (with quote PDF)"
                      variant="ghost"
                      onClick={() => handleUpdateEmiStatus("documents_pending")}
                      width="260px"
                      sx={{ textTransform: "none" }}
                      disabled={updatingEmiStatus}
                    />
                  )}
                  {order.emi?.status !== "under_review" && (
                    <Btn
                      text="Mark under review"
                      variant="ghost"
                      onClick={() => handleUpdateEmiStatus("under_review")}
                      width="200px"
                      sx={{ textTransform: "none" }}
                      disabled={updatingEmiStatus}
                    />
                  )}
                  <Btn
                    text="Mark approved"
                    variant="ghost"
                    onClick={() => handleUpdateEmiStatus("approved")}
                    width="160px"
                    sx={{ textTransform: "none" }}
                    disabled={updatingEmiStatus}
                  />
                  <Btn
                    text="Mark rejected"
                    variant="ghost"
                    onClick={openEmiRejectDialog}
                    width="160px"
                    sx={{ textTransform: "none" }}
                    disabled={updatingEmiStatus}
                  />
                </Box>
              )}
                </>
              )}
            </Card>
          )}

          {canSee("commercials") &&
            order.quotePdf?.signed &&
            (paymentClearedForAgreement || order.purchaseAgreement?.signed) && (
          <Card>
            <CardHeader
              title="Purchase agreement"
              tag={
                order.purchaseAgreement?.signed ? (
                  <Tag tone="g">SIGNED</Tag>
                ) : order.purchaseAgreement?.sent ? (
                  <Tag tone="s">SENT</Tag>
                ) : null
              }
            />
            <Box sx={{ mt: 1 }}>
              {!order.purchaseAgreement?.sent ? (
                <>
                  <Box sx={{ fontSize: 13, color: UI.ink3, marginBottom: "14px" }}>
                    Send the agreement to the signatory first — mockup and onboarding calls
                    unlock as soon as it's sent, without waiting for the signed copy to come back.
                  </Box>
                  <Btn
                    text="Send purchase agreement"
                    variant="ghost"
                    onClick={handleSendPurchaseAgreement}
                    width="220px"
                    sx={{ textTransform: "none" }}
                    disabled={sendingAgreement}
                  />
                </>
              ) : (
                <>
              <Box sx={{ fontSize: 13, color: UI.ink3, marginBottom: "14px" }}>
                No e-sign integration yet. Record it here once the countersigned
                copy is in the controlled document store.
              </Box>
              {order.purchaseAgreement?.signed ? (
                <Btn
                  text="Mark not signed"
                  variant="ghost"
                  onClick={() => handleSetAgreementSigned(false)}
                  width="180px"
                  sx={{ textTransform: "none" }}
                  disabled={updatingAgreementSigned}
                />
              ) : (
                <Btn
                  text="Mark agreement signed"
                  variant="ghost"
                  onClick={() => handleSetAgreementSigned(true)}
                  width="220px"
                  sx={{ textTransform: "none" }}
                  disabled={updatingAgreementSigned}
                />
              )}

              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderTopColor: UI.line }}>
                <Row
                  label="Countersigned PDF"
                  value={
                    order.purchaseAgreement?.documentUrl ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={handleOpenPurchaseAgreement}
                        sx={pdfLinkSx}
                      >
                        {order.purchaseAgreement.documentFileName || "Open"}
                      </Box>
                    ) : (
                      <Tag tone="i">Not uploaded</Tag>
                    )
                  }
                  last
                />
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginTop: "12px",
                  }}
                >
                  <Box
                    component="input"
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setPurchaseAgreementFile(e.target.files?.[0] || null)}
                    sx={{ fontSize: 13 }}
                  />
                  <Btn
                    text={order.purchaseAgreement?.documentUrl ? "Replace PDF" : "Upload PDF"}
                    variant="ghost"
                    onClick={handleUploadPurchaseAgreement}
                    width="160px"
                    sx={{ textTransform: "none" }}
                    disabled={uploadingPurchaseAgreement}
                  />
                </Box>
              </Box>
                </>
              )}
            </Box>
          </Card>
          )}

          {canSee("onboardingCall") && order.status !== "agreement" && (
            <Card>
              <CardHeader
                title="Onboarding call"
                tag={
                  <Tag
                    tone={
                      order.onboardingCall?.confirmed
                        ? "g"
                        : CALL_STATUS_TAG[order.onboardingCall?.status]?.tone || "i"
                    }
                  >
                    {order.onboardingCall?.confirmed
                      ? "CONFIRMED"
                      : CALL_STATUS_TAG[order.onboardingCall?.status]?.label ||
                        "NOT OFFERED"}
                  </Tag>
                }
              />
              {order.onboardingCall?.status === "not_offered" && (
                <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                  Slots not offered yet. Offer them from the queue.
                </Box>
              )}
              {order.onboardingCall?.status === "offered" && (
                <>
                  <Row
                    label="Offered window"
                    value={`${
                      order.onboardingCall?.offerFrom
                        ? new Date(
                            order.onboardingCall.offerFrom,
                          ).toLocaleDateString()
                        : "—"
                    } to ${
                      order.onboardingCall?.offerTo
                        ? new Date(
                            order.onboardingCall.offerTo,
                          ).toLocaleDateString()
                        : "—"
                    }`}
                  />
                  <Row
                    label="Call length"
                    value={
                      order.onboardingCall?.callLengthMinutes
                        ? `${order.onboardingCall.callLengthMinutes} min`
                        : "—"
                    }
                  />
                  <Box
                    sx={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      marginTop: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      label="Pick slot as customer"
                      type="datetime-local"
                      size="small"
                      value={testCallSlot}
                      onChange={(e) => setTestCallSlot(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Btn
                      text="Select this slot"
                      variant="ghost"
                      onClick={handleTestSelectCallSlot}
                      width="160px"
                      sx={{ textTransform: "none" }}
                      disabled={submittingTestCallSlot}
                    />
                  </Box>
                </>
              )}
              {order.onboardingCall?.selectedSlot && (
                <Row
                  label="Selected slot"
                  value={new Date(
                    order.onboardingCall.selectedSlot,
                  ).toLocaleString()}
                />
              )}
            </Card>
          )}

          {canSeeMockup && order.purchaseAgreement?.signed && (
            <Card>
              <CardHeader title="Clinic logo" />
              {order.logo?.url ? (
                <Row
                  label="Uploaded"
                  value={
                    mockupPreview?.logoUrl ? (
                      <Box
                        component="a"
                        href={mockupPreview.logoUrl}
                        target="_blank"
                        rel="noreferrer"
                        sx={pdfLinkSx}
                      >
                        <OpenInNewIcon sx={{ fontSize: 15 }} />
                        {order.logo.fileName || "Clinic logo"}
                      </Box>
                    ) : (
                      order.logo.fileName || "—"
                    )
                  }
                />
              ) : (
                <Box sx={{ fontSize: 13, color: UI.ink3, marginBottom: "14px" }}>
                  Nothing uploaded yet.
                </Box>
              )}
              {!order.logo?.url && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    component="input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setTestLogoFile(e.target.files?.[0] || null)}
                    sx={{ fontSize: 13 }}
                  />
                  <Btn
                    text="Upload as customer"
                    variant="ghost"
                    onClick={handleTestUploadLogo}
                    width="200px"
                    sx={{ textTransform: "none" }}
                    disabled={submittingTestLogo}
                  />
                </Box>
              )}
            </Card>
          )}

          {(order.status === "ready_to_dispatch" || order.status === "dispatched") &&
            paymentRestricted &&
            canSeeBalanceGate && (
            <Card>
              <CardHeader title="Balance status" />
              <Row
                label="Balance outstanding"
                value={
                  advancePayment?.balanceCleared ? (
                    <Tag tone="g">Cleared</Tag>
                  ) : (
                    <Tag tone="b">{rupee(advancePayment?.balanceDue)} outstanding</Tag>
                  )
                }
              />
              <Row
                label="Dispatch gate"
                value={advancePayment?.balanceCleared ? "Clear to ship" : "Blocked"}
              />
              <Box sx={{ fontSize: 11, color: UI.ink3, marginTop: "10px" }}>
                You see the gate state only. Amounts, references and receipts
                are restricted.
              </Box>
              {order.hold?.held && (
                <Box
                  sx={{
                    padding: "10px 13px",
                    borderRadius: "4px",
                    bgcolor: UI.redBg,
                    border: "1px solid",
                    borderColor: UI.red,
                    color: UI.redInk,
                    fontSize: 12,
                    marginTop: "12px",
                  }}
                >
                  Dispatch is on hold. {order.hold.reason || "No reason logged."}
                </Box>
              )}
            </Card>
          )}

          {(order.status === "ready_to_dispatch" || order.status === "dispatched") &&
            !paymentRestricted &&
            order.dispatch?.datesSetAt && (
            <Card>
              <CardHeader
                title="Balance payment"
                tag={
                  balanceEntry && (
                    <Tag
                      tone={
                        balanceEntry.status === "paid"
                          ? "g"
                          : balanceEntry.status === "link_created"
                            ? "s"
                            : "i"
                      }
                    >
                      {balanceEntry.status === "paid"
                        ? "PAID"
                        : balanceEntry.status === "link_created"
                          ? "UNPAID"
                          : "NOT REQUESTED"}
                    </Tag>
                  )
                }
              />

              {balanceEntry?.status === "paid" ? (
                <React.Fragment>
                  <Row label="Amount" value={rupee(balanceEntry.amount)} />
                  <Row
                    label="Paid on"
                    value={
                      balanceEntry.paidAt
                        ? new Date(balanceEntry.paidAt).toLocaleString()
                        : "—"
                    }
                  />
                </React.Fragment>
              ) : balanceEntry?.status === "link_created" ? (
                <React.Fragment>
                  <Row label="Amount" value={rupee(balanceEntry.amount)} />
                  <Row
                    label="Balance due date"
                    value={
                      order.dispatch?.balanceDueDate
                        ? new Date(order.dispatch.balanceDueDate).toLocaleDateString()
                        : "—"
                    }
                  />
                  <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Btn
                      text="Copy link"
                      onClick={() => handleCopyLink(balanceEntry.razorpayLinkUrl)}
                      width="140px"
                      sx={{ textTransform: "none" }}
                    />
                    <Btn
                      text="Open link"
                      onClick={() => window.open(balanceEntry.razorpayLinkUrl, "_blank")}
                      width="140px"
                      sx={{ textTransform: "none" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid",
                      borderTopColor: UI.line,
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      label="New due date (optional)"
                      type="date"
                      value={balanceResendDueDate}
                      onChange={(e) => setBalanceResendDueDate(e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 190 }}
                    />
                    <Tooltip
                      title={balanceResendDueDate ? "Save this due date without resending the link" : "Pick a new due date first"}
                      arrow
                    >
                      <span>
                        <Btn
                          text="Save"
                          variant={balanceResendDueDate ? "solid" : "ghost"}
                          onClick={handleSaveBalanceDueDate}
                          width="90px"
                          sx={{ textTransform: "none" }}
                          disabled={savingBalanceDueDate || !balanceResendDueDate}
                        />
                      </span>
                    </Tooltip>
                    <Btn
                      text="Resend link"
                      onClick={handleResendBalanceLink}
                      width="140px"
                      sx={{ textTransform: "none" }}
                      disabled={resendingBalanceLink}
                    />
                    <Btn
                      text="Cancel link"
                      variant="ghost"
                      onClick={handleCancelBalanceLink}
                      width="140px"
                      sx={{ textTransform: "none", color: UI.danger || "#c62828" }}
                      disabled={cancellingBalanceLink}
                    />
                  </Box>
                </React.Fragment>
              ) : (
                <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                  No balance payment link created yet. Sent automatically when
                  the order is marked ready to ship, from the queue.
                </Box>
              )}

              {order.hold?.held && (
                <Box
                  sx={{
                    padding: "10px 13px",
                    borderRadius: "4px",
                    bgcolor: UI.redBg,
                    border: "1px solid",
                    borderColor: UI.red,
                    color: UI.redInk,
                    fontSize: 12,
                    marginTop: "12px",
                  }}
                >
                  Dispatch is on hold. {order.hold.reason || "No reason logged."}
                </Box>
              )}
            </Card>
          )}

          {canSee("documents") && (order.invoice?.zohoInvoiceId || order.invoice?.uploadedManually) && (
            <Card>
              <CardHeader
                title="Tax invoice"
                tag={
                  <Tag tone="g">
                    {order.invoice?.uploadedManually ? "Uploaded" : "Raised"}
                  </Tag>
                }
              />
              {!order.invoice?.uploadedManually && (
                <Row label="Invoice number" value={order.invoice.invoiceNumber || "—"} />
              )}
              <Row
                label={order.invoice?.uploadedManually ? "Uploaded on" : "Raised on"}
                value={
                  order.invoice.raisedAt
                    ? new Date(order.invoice.raisedAt).toLocaleDateString()
                    : "—"
                }
              />
              {!order.invoice?.uploadedManually && (
                <Row
                  label="Emailed to doctor"
                  value={
                    order.invoice.emailedAt ? (
                      <Tag tone="g">{new Date(order.invoice.emailedAt).toLocaleDateString()}</Tag>
                    ) : (
                      <Tag tone="i">Not yet</Tag>
                    )
                  }
                />
              )}
              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Btn
                  text="Open"
                  variant="ghost"
                  onClick={handleOpenInvoice}
                  sx={{ textTransform: "none" }}
                  disabled={!order.invoice.url}
                />
                <Btn
                  text="Download"
                  variant="ghost"
                  onClick={handleDownloadInvoice}
                  sx={{ textTransform: "none" }}
                  disabled={!order.invoice.url}
                />
                {!order.invoice?.uploadedManually && (
                  <>
                    <Btn
                      text={emailingInvoice ? "Emailing…" : "Email to doctor"}
                      variant="ghost"
                      onClick={handleEmailInvoice}
                      sx={{ textTransform: "none" }}
                      disabled={emailingInvoice || !order.invoice.url}
                    />
                    {!order.invoice.emailedAt && (
                      <Btn
                        text="Edit"
                        variant="ghost"
                        onClick={() => setEditInvoiceOpen(true)}
                        sx={{ textTransform: "none" }}
                      />
                    )}
                    {advancePayment?.paymentStatus === "paid_fully" && (
                      <Btn
                        text={refreshingInvoice ? "Refreshing…" : "Refresh invoice"}
                        variant="ghost"
                        onClick={handleRefreshInvoice}
                        sx={{ textTransform: "none" }}
                        disabled={refreshingInvoice}
                      />
                    )}
                  </>
                )}
              </Box>
            </Card>
          )}

          {canSee("documents") &&
            !order.invoice?.zohoInvoiceId &&
            !order.invoice?.uploadedManually && (
              <Card>
                <CardHeader title="Tax invoice" tag={<Tag tone="i">Not raised</Tag>} />
                <Box sx={{ fontSize: 13, color: UI.ink3, mb: "12px" }}>
                  Already created this invoice directly in Zoho? Upload the PDF
                  here instead of raising a second one.
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    component="input"
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                    sx={{ fontSize: 13 }}
                  />
                  <Btn
                    text="Upload PDF"
                    variant="ghost"
                    onClick={handleUploadInvoice}
                    width="160px"
                    sx={{ textTransform: "none" }}
                    disabled={uploadingInvoice}
                  />
                </Box>
              </Card>
            )}

          {order.dispatch?.datesSetAt && (
            <Card>
              <CardHeader
                title="Fulfilment"
                tag={
                  canEditFulfilment ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setEditFulfilmentOpen(true)}
                      sx={btnSx("ghost")}
                    >
                      Edit
                    </Box>
                  ) : null
                }
              />
              <Row
                label="Shipping date"
                value={
                  order.dispatch.shippingDate
                    ? new Date(order.dispatch.shippingDate).toLocaleDateString()
                    : "—"
                }
              />
              <Row label="Unit serial" value={order.dispatch.serial || "Not allocated"} />
              <Row label="Dispatch origin" value={order.dispatch.dispatchOrigin || "—"} />
              <Row label="Courier" value={order.dispatch.courier || "—"} />
              <Row
                label="AWB Number"
                value={
                  order.dispatch.trackingId ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                      <span>{order.dispatch.trackingId}</span>
                      {order.dispatch.trackingUrl && (
                        <Box
                          component="a"
                          href={order.dispatch.trackingUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={pdfLinkSx}
                        >
                          Track
                        </Box>
                      )}
                    </Box>
                  ) : (
                    "—"
                  )
                }
              />
              <Row
                label="Dispatched"
                value={
                  order.dispatch.dispatchDate
                    ? new Date(order.dispatch.dispatchDate).toLocaleDateString()
                    : "—"
                }
              />
              <Row
                label="Expected delivery"
                value={
                  order.dispatch.expectedDelivery
                    ? new Date(order.dispatch.expectedDelivery).toLocaleDateString()
                    : "—"
                }
              />
              <Row
                label="Technician"
                value={
                  order.technician?.name ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-end" }}>
                      {order.technician.name}
                      {order.technician.status === "declined" && <Tag tone="b">Declined</Tag>}
                      {order.technician.status === "offered" && <Tag tone="s">Awaiting response</Tag>}
                    </Box>
                  ) : (
                    <Tag tone="b">Unassigned</Tag>
                  )
                }
              />
              <Row
                label="Arrival"
                value={
                  order.travel?.arrivalDate
                    ? `${new Date(order.travel.arrivalDate).toLocaleDateString()}${
                        order.travel.slot ? `, ${order.travel.slot}` : ""
                      }`
                    : "—"
                }
                last
              />
            </Card>
          )}

          {canSeeSiteDetail && order.technician?.name && (
            <Card>
              <CardHeader
                title="Site and installation"
                tag={
                  order.status === "activated" ? (
                    <Tag tone="g">Activated</Tag>
                  ) : order.status === "installation" ? (
                    <Tag tone="s">Installing</Tag>
                  ) : null
                }
              />
              <Row label="Clinic" value={order.clinic?.name || "—"} />
              <Row
                label="Clinic mobile"
                value={order.clinic?.mobile ? `${order.clinic?.countryCode || ""}${order.clinic.mobile}` : "—"}
              />
              <Row label="Doctor" value={order.doctor?.name || "—"} />
              <Row
                label="Doctor mobile"
                value={order.doctor?.mobile ? `${order.doctor?.countryCode || ""}${order.doctor.mobile}` : "—"}
              />
              <Row label="Installation window" value={order.technician?.installationWindow || "—"} />
              <Row
                label="Google Maps location"
                value={
                  order.technician?.mapsLink ? (
                    <Box
                      component="a"
                      href={order.technician.mapsLink}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: UI.p }}
                    >
                      Open
                    </Box>
                  ) : (
                    "—"
                  )
                }
              />
              <Row label="Activation code" value={order.activationCode || "—"} />
              <Row label="Site notes" value={order.technician?.siteNotes || "—"} />
              <Row label="Payment due" value={order.technician?.paymentDue || "—"} />
              <Box
                sx={{
                  margin: "10px 0",
                  padding: "12px 14px",
                  borderRadius: "8px",
                  borderLeft: `3px solid ${UI.p}`,
                  bgcolor: UI.bg2,
                  fontSize: 13,
                  color: UI.ink,
                  lineHeight: 1.7,
                }}
              >
                <Box sx={{ fontWeight: 600, marginBottom: "8px" }}>Payment on site</Box>
                <Box>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Cheque
                  </Box>
                  {' — of same date, favoring "Trismus Healthcare Technologies Pvt Ltd".'}
                </Box>
                <Box sx={{ marginTop: "10px" }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Via NEFT
                  </Box>
                  {" — on the following bank account:"}
                </Box>
                <Box sx={{ marginTop: "6px", paddingLeft: "12px", display: "grid", rowGap: "3px" }}>
                  <Box>
                    <Box component="span" sx={{ fontWeight: 600, color: UI.ink3 }}>
                      Account Name:
                    </Box>{" "}
                    Trismus Healthcare Technologies Pvt. Ltd.
                  </Box>
                  <Box>
                    <Box component="span" sx={{ fontWeight: 600, color: UI.ink3 }}>
                      Account Number:
                    </Box>{" "}
                    <Box component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>
                      3945264919
                    </Box>
                  </Box>
                  <Box>
                    <Box component="span" sx={{ fontWeight: 600, color: UI.ink3 }}>
                      Bank Name:
                    </Box>{" "}
                    Kotak Mahindra Bank Ltd.
                  </Box>
                  <Box>
                    <Box component="span" sx={{ fontWeight: 600, color: UI.ink3 }}>
                      Branch:
                    </Box>{" "}
                    Baner, Pune
                  </Box>
                  <Box>
                    <Box component="span" sx={{ fontWeight: 600, color: UI.ink3 }}>
                      IFSC:
                    </Box>{" "}
                    <Box component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>
                      KKBK0001767
                    </Box>
                  </Box>
                </Box>
                {isTechnicianViewer && (
                  <Box sx={{ marginTop: "12px" }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      Via UPI
                    </Box>
                    {" — scan with any UPI app:"}
                    <Box sx={{ marginTop: "8px" }}>
                      <Box
                        component="img"
                        src={scanOUpiQr}
                        alt="scanO UPI QR code"
                        sx={{ width: 160, height: "auto", borderRadius: "6px", display: "block" }}
                      />
                      <Box sx={{ marginTop: "4px", fontFamily: "monospace", fontSize: 13 }}>
                        dentaldost@kotak
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
              <Row
                label="Inauguration"
                value={
                  order.inauguration?.date
                    ? `${new Date(order.inauguration.date).toLocaleDateString()}${
                        order.inauguration.influencer ? ` · ${order.inauguration.influencer}` : ""
                      }${order.inauguration.completedAt ? " · Complete" : ""}`
                    : "—"
                }
              />
              <Row
                label="Activation"
                value={
                  order.activation?.activatedAt ? (
                    <Tag tone="g">Activated {new Date(order.activation.activatedAt).toLocaleDateString()}</Tag>
                  ) : (
                    <Tag tone="i">Not activated</Tag>
                  )
                }
              />
              <Row
                label="Checklist"
                value={
                  order.checklist?.submittedAt ? (
                    <Tag tone="g">Submitted</Tag>
                  ) : order.activation?.activatedAt ? (
                    <Tag tone="b">Pending</Tag>
                  ) : (
                    <Tag tone="i">Not started</Tag>
                  )
                }
              />
              {order.checklist?.submittedAt && order.checklist?.photos?.length > 0 && (
                <Row
                  label="Site photos"
                  value={
                    checklistPhotos ? (
                      <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {checklistPhotos.map((photo, i) => (
                          <Box
                            component="button"
                            key={photo.url}
                            type="button"
                            onClick={() => setLightboxIndex(i)}
                            title={CHECKLIST_PHOTO_LABELS[photo.type] || "Photo"}
                            sx={{
                              width: 56,
                              height: 56,
                              padding: 0,
                              border: "1px solid",
                              borderColor: UI.line,
                              borderRadius: "6px",
                              overflow: "hidden",
                              cursor: "pointer",
                              bgcolor: UI.bg3,
                              "&:hover": { borderColor: UI.p },
                            }}
                          >
                            <Box
                              component="img"
                              src={photo.url}
                              alt={CHECKLIST_PHOTO_LABELS[photo.type] || `Photo ${i + 1}`}
                              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box
                        component="button"
                        type="button"
                        onClick={handleLoadChecklistPhotos}
                        sx={pdfLinkSx}
                        disabled={loadingChecklistPhotos}
                      >
                        {loadingChecklistPhotos
                          ? "Loading…"
                          : `View photos (${order.checklist.photos.length})`}
                      </Box>
                    )
                  }
                />
              )}
              {order.checklist?.submittedAt && order.checklist?.observations && (
                <Row
                  label="Technician notes"
                  value={
                    <Box sx={{ textAlign: "right", fontSize: 12, color: UI.ink3, maxWidth: 280 }}>
                      "{order.checklist.observations}"
                    </Box>
                  }
                />
              )}
              <Row
                label="Handover"
                value={
                  order.handover?.acceptedAt ? (
                    `Accepted by ${order.handover.acceptedBy}`
                  ) : order.warranty?.issuedAt ? (
                    <Tag tone="b">Awaiting acceptance</Tag>
                  ) : (
                    <Tag tone="i">Not yet</Tag>
                  )
                }
                last={!order.feedback?.submittedAt && !(order.checklist?.submittedAt && !order.warranty?.issuedAt)}
              />
              {order.feedback?.submittedAt && (
                <Row
                  label="Customer feedback"
                  value={
                    <Box sx={{ textAlign: "right" }}>
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {order.feedback.rating}/5
                      </Box>
                      {order.feedback.comment && (
                        <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px" }}>
                          "{order.feedback.comment}"
                        </Box>
                      )}
                    </Box>
                  }
                  last={!(order.checklist?.submittedAt && !order.warranty?.issuedAt)}
                />
              )}
            </Card>
          )}

          <Dialog
            open={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { ...dialogPaperSx, bgcolor: "#000" } }}
          >
            {lightboxIndex !== null && checklistPhotos?.[lightboxIndex] && (
              <>
                <DialogTitle sx={{ ...dialogTitleSx, borderColor: "rgba(255,255,255,0.12)" }}>
                  <DialogHeading
                    title={CHECKLIST_PHOTO_LABELS[checklistPhotos[lightboxIndex].type] || "Photo"}
                    sub={`${lightboxIndex + 1} of ${checklistPhotos.length}`}
                  />
                  <IconButton onClick={() => setLightboxIndex(null)} size="small">
                    <CloseIcon sx={{ fontSize: 18, color: "#fff" }} />
                  </IconButton>
                </DialogTitle>
                <DialogContent
                  sx={{
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    bgcolor: "#000",
                    minHeight: 320,
                  }}
                >
                  <Box
                    component="img"
                    src={checklistPhotos[lightboxIndex].url}
                    alt={CHECKLIST_PHOTO_LABELS[checklistPhotos[lightboxIndex].type] || "Photo"}
                    sx={{ maxWidth: "100%", maxHeight: "75vh", display: "block", margin: "0 auto" }}
                  />
                  {checklistPhotos.length > 1 && (
                    <>
                      <IconButton
                        onClick={() =>
                          setLightboxIndex((i) => (i - 1 + checklistPhotos.length) % checklistPhotos.length)
                        }
                        sx={{
                          position: "absolute",
                          left: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                      >
                        ‹
                      </IconButton>
                      <IconButton
                        onClick={() => setLightboxIndex((i) => (i + 1) % checklistPhotos.length)}
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                      >
                        ›
                      </IconButton>
                    </>
                  )}
                </DialogContent>
              </>
            )}
          </Dialog>

          <Dialog
            open={modalOpen}
            onClose={closeAdvanceModal}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: dialogPaperSx }}
          >
            <DialogTitle sx={dialogTitleSx}>
              <DialogHeading
                title={
                  advanceConfirmStep
                    ? "Confirm before proceeding"
                    : "Create advance payment link"
                }
                sub={order.clinic?.address || order.clinic?.name || "—"}
              />
              <IconButton onClick={closeAdvanceModal} size="small">
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "18px" }}>
              {order.quotePdf?.signed && (
                <Box
                  sx={{
                    padding: "10px 13px",
                    borderRadius: "4px",
                    bgcolor: UI.greenBg,
                    border: "1px solid",
                    borderColor: UI.green,
                    color: UI.greenInk,
                    fontSize: 12,
                    marginBottom: "18px",
                  }}
                >
                  Customer accepted the quotation.
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: "10px",
                  marginBottom: "18px",
                }}
              >
                <Stat
                  label="Payment term"
                  value={order.quote?.paymentTerm || "—"}
                />
                <Stat
                  label="Order total"
                  value={rupee(order.quote?.totalAmount)}
                />
              </Box>

              {advanceConfirmStep ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      padding: "14px 16px",
                      borderRadius: "6px",
                      border: "1px solid",
                      borderColor: UI.line,
                      marginBottom: "18px",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box sx={{ fontSize: 12, color: UI.ink3 }}>Price</Box>
                      <Box sx={{ fontSize: 13, fontWeight: 600 }}>
                        {rupee(Number(advanceForm.amount))}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box sx={{ fontSize: 12, color: UI.ink3 }}>Payment mode</Box>
                      <Box sx={{ fontSize: 13, fontWeight: 600 }}>
                        {advanceForm.paymentMode === "bank_transfer"
                          ? "Bank transfer, manual reconciliation"
                          : "Razorpay link"}
                      </Box>
                    </Box>
                    {advanceForm.paymentMode === "bank_transfer" ? (
                      <>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Box sx={{ fontSize: 12, color: UI.ink3 }}>Zoho record</Box>
                          <Box sx={{ fontSize: 13, fontWeight: 600 }}>
                            {advanceForm.alreadyRecordedInZoho
                              ? "Already recorded — no new entry"
                              : "Will create a new entry"}
                          </Box>
                        </Box>
                        {!advanceForm.alreadyRecordedInZoho && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Box sx={{ fontSize: 12, color: UI.ink3 }}>Receipt email</Box>
                            <Box sx={{ fontSize: 13, fontWeight: 600 }}>
                              {advanceForm.skipReceipt
                                ? "Won't be sent"
                                : "Will be emailed to doctor"}
                            </Box>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Box sx={{ fontSize: 12, color: UI.ink3 }}>Due date</Box>
                        <Box sx={{ fontSize: 13, fontWeight: 600 }}>
                          {advanceForm.dueDate || "—"}
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ fontSize: 13, color: UI.ink2, marginBottom: "18px" }}>
                    {advanceForm.paymentMode === "bank_transfer"
                      ? "Are you sure? This marks the amount as received right away."
                      : "Are you sure? This sends a real payment link to the doctor."}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column-reverse", sm: "row" },
                      justifyContent: "flex-end",
                      gap: 1,
                      mt: 3,
                    }}
                  >
                    <Btn
                      text="Go back"
                      variant="ghost"
                      onClick={() => setAdvanceConfirmStep(false)}
                      width="120px"
                      sx={{
                        textTransform: "none",
                        width: { xs: "100%", sm: "120px" },
                      }}
                    />
                    <Btn
                      text={
                        advanceForm.paymentMode === "bank_transfer"
                          ? "Yes, record payment"
                          : "Yes, create link"
                      }
                      onClick={handleCreateAdvanceLink}
                      width="200px"
                      sx={{
                        textTransform: "none",
                        width: { xs: "100%", sm: "200px" },
                      }}
                      disabled={submitting}
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      label="Advance amount"
                      type="number"
                      value={advanceForm.amount}
                      onChange={(e) =>
                        setAdvanceForm((prev) => ({
                          ...prev,
                          amount: e.target.value,
                        }))
                      }
                      fullWidth
                    />
                    <TextField
                      select
                      label="Payment mode"
                      value={advanceForm.paymentMode}
                      onChange={(e) =>
                        setAdvanceForm((prev) => ({
                          ...prev,
                          paymentMode: e.target.value,
                        }))
                      }
                      fullWidth
                    >
                      <MenuItem value="razorpay">Razorpay link</MenuItem>
                      <MenuItem value="bank_transfer">
                        Bank transfer, manual reconciliation
                      </MenuItem>
                    </TextField>
                    {advanceForm.paymentMode === "bank_transfer" ? (
                      <>
                        <TextField
                          select
                          label="Zoho record"
                          value={advanceForm.alreadyRecordedInZoho ? "already" : "create"}
                          onChange={(e) =>
                            setAdvanceForm((prev) => ({
                              ...prev,
                              alreadyRecordedInZoho: e.target.value === "already",
                            }))
                          }
                          helperText={
                            advanceForm.alreadyRecordedInZoho
                              ? "We'll only save this here — no new payment entry created in Zoho."
                              : undefined
                          }
                          fullWidth
                        >
                          <MenuItem value="create">Create payment entry in Zoho</MenuItem>
                          <MenuItem value="already">Already recorded in Zoho manually</MenuItem>
                        </TextField>
                        {!advanceForm.alreadyRecordedInZoho && (
                          <TextField
                            select
                            label="Payment receipt"
                            value={advanceForm.skipReceipt ? "skip" : "send"}
                            onChange={(e) =>
                              setAdvanceForm((prev) => ({
                                ...prev,
                                skipReceipt: e.target.value === "skip",
                              }))
                            }
                            helperText={
                              advanceForm.skipReceipt
                                ? "Use for a backdated/already-settled payment — doctor won't get an email."
                                : undefined
                            }
                            fullWidth
                          >
                            <MenuItem value="send">Email receipt to doctor</MenuItem>
                            <MenuItem value="skip">Don't send receipt</MenuItem>
                          </TextField>
                        )}
                      </>
                    ) : (
                      <TextField
                        label="Due date"
                        type="date"
                        value={advanceForm.dueDate}
                        onChange={(e) =>
                          setAdvanceForm((prev) => ({
                            ...prev,
                            dueDate: e.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column-reverse", sm: "row" },
                      justifyContent: "flex-end",
                      gap: 1,
                      mt: 3,
                    }}
                  >
                    <Btn
                      text="Cancel"
                      variant="ghost"
                      onClick={closeAdvanceModal}
                      width="120px"
                      sx={{
                        textTransform: "none",
                        width: { xs: "100%", sm: "120px" },
                      }}
                    />
                    <Btn
                      text="Continue"
                      onClick={handleReviewAdvanceLink}
                      width="200px"
                      sx={{
                        textTransform: "none",
                        width: { xs: "100%", sm: "200px" },
                      }}
                    />
                  </Box>
                </>
              )}
            </DialogContent>
          </Dialog>

          <Dialog
            open={quoteModalOpen}
            onClose={() => setQuoteModalOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: dialogPaperSx }}
          >
            <DialogTitle sx={dialogTitleSx}>
              <DialogHeading
                title="Review and send quotation"
                sub={order.clinic?.address || order.clinic?.name || "—"}
              />
              <IconButton onClick={() => setQuoteModalOpen(false)} size="small">
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "18px" }}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: UI.line,
                  borderRadius: "6px",
                  padding: "12px 14px",
                  marginBottom: "18px",
                }}
              >
                <Row
                  label="Product subtotal"
                  value={rupee(order.quote?.grossAmount)}
                />
                <Row
                  label="Discount"
                  value={`-${rupee(
                    order.quote?.discountType === "percent"
                      ? order.quote?.discountAmount
                      : order.quote?.discount,
                  )}`}
                />
                {Number(order.quote?.engageGrossAmount) > 0 && (
                  <React.Fragment>
                    <Row
                      label="Engage subscription"
                      value={rupee(order.quote?.engageGrossAmount)}
                    />
                    <Row
                      label="Engage discount"
                      value={`-${rupee(
                        order.quote?.engageDiscountType === "percent"
                          ? order.quote?.engageDiscountAmount
                          : order.quote?.engageDiscount,
                      )}`}
                    />
                  </React.Fragment>
                )}
                {(order.quote?.engageAddOns || []).map((addOn, index) =>
                  Number(addOn.grossAmount) > 0 ? (
                    <React.Fragment key={index}>
                      <Row
                        label={`Engage subscription ${index + 2}`}
                        value={rupee(addOn.grossAmount)}
                      />
                      <Row
                        label={`Engage discount ${index + 2}`}
                        value={`-${rupee(
                          addOn.discountType === "percent"
                            ? addOn.discountAmount
                            : addOn.discount,
                        )}`}
                      />
                    </React.Fragment>
                  ) : null,
                )}
                {order.quote?.bagRequired && (
                  <React.Fragment>
                    <Row
                      label="Carry bag"
                      value={rupee(order.quote?.bagGrossAmount)}
                    />
                    {Number(order.quote?.bagDiscount) > 0 && (
                      <Row
                        label="Bag discount"
                        value={`-${rupee(
                          order.quote?.bagDiscountType === "percent"
                            ? order.quote?.bagDiscountAmount
                            : order.quote?.bagDiscount,
                        )}`}
                      />
                    )}
                  </React.Fragment>
                )}
                <Row label="GST" value={rupee(order.quote?.gstAmount)} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingTop: "9px",
                    marginTop: "5px",
                    borderTop: "1px solid",
                    borderTopColor: UI.line,
                  }}
                >
                  <Box sx={{ fontSize: 14, fontWeight: 500, color: UI.ink }}>
                    Total
                  </Box>
                  <Box sx={{ fontSize: 14, fontWeight: 500, color: UI.ink }}>
                    {rupee(order.quote?.totalAmount)}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Stat
                  label="Payment terms"
                  value={order.quote?.paymentTerm || "—"}
                />
                <Stat
                  label="Place of supply"
                  value={order.placeOfSupply || "—"}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Checkbox
                  checked={pricingChecked}
                  onChange={(e) => setPricingChecked(e.target.checked)}
                  sx={{ mt: -1 }}
                />
                <Box>
                  <Box sx={{ fontSize: 13, color: UI.ink }}>
                    Pricing, discount and GST checked against the approved price
                    list
                  </Box>
                  <Box sx={{ fontSize: 11, color: UI.ink3 }}>
                    Sending locks the quotation.
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 3,
                }}
              >
                <Btn
                  text="Cancel"
                  variant="ghost"
                  onClick={() => setQuoteModalOpen(false)}
                  width="120px"
                  sx={{
                    textTransform: "none",
                    width: { xs: "100%", sm: "120px" },
                  }}
                />
                <Btn
                  text="Send quotation"
                  onClick={handleSendQuotation}
                  width="200px"
                  sx={{
                    textTransform: "none",
                    width: { xs: "100%", sm: "200px" },
                  }}
                  disabled={submittingQuote}
                />
              </Box>
            </DialogContent>
          </Dialog>
        </Box>

        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "18px" }}>
          {canSee("documents") ? (
            <DocumentsPane
              order={order}
              onOpenQuote={handleOpenQuote}
              onOpenInvoice={handleOpenInvoice}
              onOpenMockup={handleOpenMockup}
              onDownloadMockup={handleDownloadMockup}
              onOpenPurchaseAgreement={handleOpenPurchaseAgreement}
            />
          ) : (
            <Restrict what="Documents" />
          )}

          <Card>
            <CardHeader title="Activity" />
            {order.timeline?.length ? (
              order.timeline.map((entry, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "11px 1fr",
                    gap: "11px",
                    paddingBottom: "14px",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: "3px",
                      top: "11px",
                      bottom: 0,
                      width: "1px",
                      bgcolor: UI.line,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: UI.p,
                      marginTop: "5px",
                      position: "relative",
                      zIndex: 1,
                    }}
                  />
                  <Box>
                    <Box sx={{ fontSize: 12, color: UI.ink }}>{entry.text}</Box>
                    <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "1px" }}>
                      {entry.by} · {new Date(entry.at).toLocaleString()}
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ fontSize: 13, color: UI.ink3 }}>No activity yet.</Box>
            )}
          </Card>
        </Box>
      </Box>

      <EditQuoteModal
        open={editQuoteOpen}
        orderId={id}
        onClose={() => setEditQuoteOpen(false)}
        onSaved={(updatedOrder) => {
          setEditQuoteOpen(false);
          applyOrderResponse(updatedOrder);
        }}
      />

      <EditQuoteModal
        open={editInvoiceOpen}
        orderId={id}
        mode="invoice"
        onClose={() => setEditInvoiceOpen(false)}
        onSaved={(updatedOrder) => {
          setEditInvoiceOpen(false);
          applyOrderResponse(updatedOrder);
        }}
      />

      {/* EMI REJECT REASON DIALOG */}
      <Dialog
        open={emiRejectDialogOpen}
        onClose={() => setEmiRejectDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <DialogHeading title="Mark EMI rejected" />
          <IconButton
            onClick={() => setEmiRejectDialogOpen(false)}
            size="small"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "18px" }}>
          <TextField
            size="small"
            label="Rejection reason"
            placeholder="e.g. Income proof incomplete"
            multiline
            minRows={3}
            fullWidth
            value={emiRejectReason}
            onChange={(e) => setEmiRejectReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Btn
              text="Cancel"
              variant="ghost"
              onClick={() => setEmiRejectDialogOpen(false)}
              width="120px"
              sx={{ textTransform: "none", width: { xs: "100%", sm: "120px" } }}
            />
            <Btn
              text="Confirm rejection"
              onClick={handleConfirmEmiReject}
              width="180px"
              sx={{ textTransform: "none", width: { xs: "100%", sm: "180px" } }}
              disabled={updatingEmiStatus}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <ReviseEmiTermsModal
        open={reviseEmiOpen}
        orderId={id}
        onClose={() => setReviseEmiOpen(false)}
        onSaved={(updatedOrder) => {
          setReviseEmiOpen(false);
          applyOrderResponse(updatedOrder);
        }}
      />

      <ActionDrawer
        open={editFulfilmentOpen}
        screenKey="edit-fulfilment"
        item={{
          orderId: id,
          clinicName: order.clinic?.name,
          context: {
            shippingDate: toDateInput(order.dispatch?.shippingDate),
            dispatchOrigin: order.dispatch?.dispatchOrigin || "",
            serial: order.dispatch?.serial || "",
            courier: order.dispatch?.courier || "",
            trackingId: order.dispatch?.trackingId || "",
            dispatchDate: toDateInput(order.dispatch?.dispatchDate),
            expectedDelivery: toDateInput(order.dispatch?.expectedDelivery),
          },
        }}
        onClose={() => setEditFulfilmentOpen(false)}
        onSaved={(updatedOrder) => {
          setEditFulfilmentOpen(false);
          if (updatedOrder) applyOrderResponse(updatedOrder);
        }}
      />

    </Box>
  );
};

export default OrderDetail;
